import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import CheckmarkOutline16 from '@carbon/icons-react/es/checkmark--outline/16';
import Copy16 from '@carbon/icons-react/es/copy/16';
import Restart16 from '@carbon/icons-react/es/restart/16';
import { Space, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import styled from 'styled-components';
import { APIKeysApi, CreateApiKeyPayload } from 'api/api-keys';
import { Button, LinkButton } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { Modal } from 'components/atoms/modal';
import { useTranslation } from 'hooks/use-translation';
import { GET_WORKATO_KEY, useGetWorkatoKey } from 'queries/api-keys';
import { Label } from '../styles';

const StyledInput = styled(Input)`
  .ant-input[disabled] {
    color: var(--gray-100);
  }
`;

export const WorkatoForm = (): JSX.Element => {
  const [showCopied, setShowCopied] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const reset = useTranslation('reset');
  const buttonText = useTranslation('copy');
  const copiedText = useTranslation('copied');
  const externalButtonText = useTranslation(
    'settings.integrations.workato.visit',
  );
  const resetTooltip = useTranslation(
    'settings.integrations.zapier.input.tooltip',
  );
  const confirmTitle = useTranslation(
    'settings.integration.zapier.reset.confirm.title',
  );
  const confirmContent = useTranslation(
    'settings.integration.workato.reset.confirm.content',
  );
  const helpText = useTranslation('settings.integrations.workato.input.help');
  const labelText = useTranslation('settings.integrations.zapier.input.label');

  const queryClient = useQueryClient();
  const { createWorkatoKey } = APIKeysApi;
  const { data } = useGetWorkatoKey();
  const createFunction = (payload: CreateApiKeyPayload) =>
    createWorkatoKey(payload);
  const { mutate, isLoading: isCreating } = useMutation(createFunction);

  const [form] = Form.useForm();

  const onReset = () => {
    mutate(
      {
        apiKeyName: 'Workato',
      },
      {
        onSuccess: (resp) => {
          form.setFieldsValue({ apiKey: resp.apiKey });
          queryClient.invalidateQueries(GET_WORKATO_KEY);
          setShowConfirm(false);
        },
      },
    );
  };

  useEffect(() => {
    form.setFieldsValue({ apiKey: data?.apiKey });
  }, [form, data]);

  return (
    <>
      <Label>{labelText}</Label>
      <Form layout="vertical" form={form} requiredMark={false}>
        <Space size={0} align="start">
          <Form.Item name="apiKey" help={helpText}>
            <StyledInput
              disabled
              suffix={
                <Tooltip title={resetTooltip}>
                  <LinkButton
                    icon={<Restart16 />}
                    onClick={() => setShowConfirm(true)}
                    disabled={isCreating}
                  />
                </Tooltip>
              }
            />
          </Form.Item>

          <Space>
            <Form.Item shouldUpdate>
              {() => (
                <Tooltip
                  title="null"
                  onVisibleChange={(visible) =>
                    !visible && setShowCopied(false)
                  }
                  mouseLeaveDelay={2}
                  overlayStyle={{
                    display: 'none',
                  }}
                >
                  <Button
                    type="primary"
                    icon={showCopied ? <CheckmarkOutline16 /> : <Copy16 />}
                    iconPlacement="right"
                    loading={isCreating}
                    onClick={() => {
                      if (data && !showCopied) {
                        navigator.clipboard.writeText(data?.apiKey);
                        setShowCopied(true);
                      }
                    }}
                  >
                    {showCopied ? copiedText : buttonText}
                  </Button>
                </Tooltip>
              )}
            </Form.Item>

            <Form.Item shouldUpdate>
              <Button
                icon={<ArrowRight16 />}
                href="https://www.workato.com/integrations/rocketlane"
                target="_blank"
                iconPlacement="right"
              >
                {externalButtonText}
              </Button>
            </Form.Item>
          </Space>
        </Space>
      </Form>
      <Modal
        visible={showConfirm}
        title={confirmTitle}
        confirmLoading={isCreating}
        onCancel={() => setShowConfirm(false)}
        onOk={onReset}
        okType="danger"
        okText={reset}
      >
        <p>{confirmContent}</p>
      </Modal>
    </>
  );
};
